import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import {
  setShowFreeSpinZeroWin,
  setShowTransitionDialog,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME, MUSIC_FADE_MS as SHARED_MUSIC_FADE_MS } from "../../../../sounds";
import { HowlCache } from "../../../../State/Slices/soundsSlice";
import TickerTimeout from "../../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle, WIN_TEXT_SCALE } from "../../../../fontStyles";
import { formatToCustomLocale } from "../../../../utilities";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import attachTapToContinue from "../../../../utils/attachTapToContinue";
import store from "../../../../State/Store/store";

// const SPINE_NAME = "win_celebration";
// const SKIN_NAME = "zero_win";
// Its own spine (animator, 2026-09-30): `zero_win` — default skin only, a glow
// behind two text slots, txt_slot_better_luck / txt_slot_next_time (see
// initializeMessage), plus value / xbet. JSON hand edits: the clips
// `better_luck_in/idle/out` renamed in / idle / out; the atlas named its page
// `better_luck.webp` (the file is zero_win.webp); the tap slot/bone
// `txt_slot_taptostart` (0, -856.5) -> `txt_slot_tap_to_continue` (0, -480.24).
const SPINE_NAME = "zero_win";
const SKIN_NAME = null;
// The two message lines of the new spine: "BETTER LUCK" / "NEXT TIME".
const MESSAGE_TOP_SLOT = "txt_slot_better_luck";
const MESSAGE_BOTTOM_SLOT = "txt_slot_next_time";
// The widest a line may be (spine units) — its slots carry no bone length.
const MESSAGE_MAX_WIDTH = 1000;

// ============================================================================
// Sizing — the WinAnimationSecondary baseline, as every Mad Labs popup takes
// it. All 1.0 until a device proves otherwise.
// ============================================================================
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the word at the SAME screen position, so opening two at
// once shows them perfectly aligned. Total win sets that position: its bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has.
//
// This spine's tap bone is at -480.24, so it needs -43.66 to reach the same line.
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// Natural size runs too large on a standard landscape phone.
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
// Desktop takes the landscape-phone treatment: same trim, same tap-word lift.
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_DESKTOP = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)

const MESSAGE_SLOTS = ["txt_slot_better_luck_next_time"];
const VALUE_SLOTS = ["txt_slot_value"];
// `txt_slot_xbet_value` on the current export; `txt_slot_multiplier` was the
// Zeus name. Tried in order — the Pot Collection popups still ask only for the
// old one and so draw no multiplier at all.
const MULTIPLIER_SLOTS = ["txt_slot_xbet_value", "txt_slot_multiplier"];
const MESSAGE_FONT_SIZE = 124;
const MESSAGE_LINE_HEIGHT = 0.95;
const BUTTON_SLOTS = ["txt_slot_tap_to_continue", "txt_slot_taptocontinue"];

// Auto-closes, as total win does — a player who walks away is not left
// staring at it. Borrowed from the Pot Collection outro's constant until free
// spins has a config block of its own.
const AUTO_CLOSE_DELAY =
  FEATURES_CONFIG.POT_COLLECTION.TOTAL_WIN_AUTO_CLOSE || 30000;

// Fallback only — input unlocks when the spine finishes revealing the tap
// word. See attachTapToContinue.revealMs.
const SKIP_PROTECTION_MS = 1000;
const CLICK_DEBOUNCE_MS = 300;
const MUSIC_FADE_MS = SHARED_MUSIC_FADE_MS;

/**
 * FreeSpinZeroWinController — the feature ended BELOW 1x the stake.
 *
 * The `zero_win` skin of the shared `win_celebration` spine. That skin carries
 * ONE attachment, `zero_win_back` — a background and nothing else — so the
 * popup is that plate plus BETTER LUCK NEXT TIME across the middle.
 *
 * ── It is not only the zero case ──
 *
 * The name is historical. The GDD's rule is a THRESHOLD, not a zero test:
 * "Total Win pop-up is displayed. Alternatively, the Better Luck Next Time
 * pop-up is displayed (when the win is lower than 1x bet)." So this popup also
 * closes a round that paid 0.4x, and it prints that figure. The choice between
 * the two is made in freeSpinOutroPopup, which both end-of-round sites call.
 *
 * Identical to FreeSpinTotalWinController except for what a short round does
 * not warrant: no counter, no run-up, no two-stage tap, and none of the win
 * music. It keeps the parts that matter — skip protection, auto-close and the
 * SYNCHRONOUS close flag that stops the end-of-feature condition from
 * re-opening it.
 *
 * Reload is not handled here, for the same reason as total win: that belongs
 * with the free-spins slice.
 */
class FreeSpinZeroWinController {
  constructor(container, dimensionsContext, dispatch, symbolHeight, overlayRef, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext?.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;
    this.inIdleTimeout = null;
    this.musicDelayTimeout = null;
    this._showRAF = null;

    this.messageText = null;
    this.messageContainer = null;
    this._messageSlotName = null;
    this.valueText = null;
    this.multiplierText = null;
    this.buttonText = null;
    this._fitTapButton = null;

    this.autoCloseTimeout = null;
    this._wasAmbientActive = false;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeSpine();
  }

  // ── Sizing ───────────────────────────────────────────────────────────────

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers =
        orientation === PixiSettings.ORIENTATION.PORTRAIT
          ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
          : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.spine.scale.set(scale);
    this.spine.x = 0;
    this.spine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

    this._fitTapButton?.(this.dimensions);
    this.applyButtonOffset();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDims = this.dimensionsContext?.dimensions;
    const newDims = dimensionsContext?.dimensions;

    const changed =
      !oldDims ||
      oldDims.width !== newDims?.width ||
      oldDims.height !== newDims?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.symbolHeight !== symbolHeight;
    if (!changed) return;

    const deviceChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;

    if (deviceChanged && this.buttonText) this.updateButtonText();
    this.applyScaleAndCenter();
    this._fitMessage();
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(`${SPINE_NAME}-skeleton`) ||
        !assets.has(`${SPINE_NAME}-atlas`)
      ) {
        console.warn("[FreeSpinZeroWin] Spine not loaded:", SPINE_NAME);
        return;
      }

      this.spine = Spine.from({
        skeleton: `${SPINE_NAME}-skeleton`,
        atlas: `${SPINE_NAME}-atlas`,
      });
      if (!this.spine?.skeleton || !this.spine?.state) return;

      this.container.addChild(this.spine);
      // Hidden by ALPHA, never by unmounting — that is what gives `out` frames.
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      this.spine.zIndex = 20;

      this.applySkin();
      this.applyScaleAndCenter();
      this.initialized = true;

      this.warmUpSpine();
      this.initializeMessage();
      this.initializeValues();
      this.initializeButton();
    } catch (error) {
      console.warn("[FreeSpinZeroWin] Init failed:", error);
    }
  }

  applySkin() {
    // Its own spine now — one picture (default skin), nothing to set.
    if (!SKIN_NAME) return;
    const data = this.spine?.skeleton?.data;
    if (!data?.findSkin(SKIN_NAME)) {
      console.warn("[FreeSpinZeroWin] Missing skin:", SKIN_NAME);
      return;
    }
    this.spine.skeleton.setSkinByName(SKIN_NAME);
    // Slots only — setToSetupPose would reset BONES and snap the pose.
    this.spine.skeleton.setSlotsToSetupPose();
  }

  warmUpSpine() {
    if (!this.spine?.state) return;
    try {
      if (this.spine) this.spine.autoUpdate = true;
      this.spine.state.setAnimation(0, "in", false);
      this.spine.state.update(0.016);
      // Spine 4.2 REQUIRES an argument here.
      if (this.spine.skeleton.physics !== undefined) {
        this.spine.skeleton.updateWorldTransform(2);
      } else {
        this.spine.skeleton.updateWorldTransform(0);
      }
      this.spine.state.clearTracks();
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  _resolveSlot(candidates) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates || []) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /** Largest font size at which `text` still fits the screen. Never scales up. */
  _fitFontSizeToWidth(text, baseStyle, baseFontSize, screenRatio = 0.85) {
    const spineScale = this.spine?.scale?.x || 1;
    const maxWidth = ((this.dimensions?.width || 800) * screenRatio) / spineScale;
    const metrics = PIXI.TextMetrics.measureText(
      text,
      new PIXI.TextStyle(scaleStyle(baseStyle, baseFontSize)),
    );
    if (metrics.width <= maxWidth || maxWidth <= 0) return baseFontSize;
    return Math.max(1, Math.round(baseFontSize * (maxWidth / metrics.width)));
  }

  /**
   * BETTER LUCK NEXT TIME, in the spine's own slot.
   *
   * win_text — the intro popup's MAX WIN — in red and larger. The bone is
   * flagged `skin: true`, so it belongs to the zero_win skin and only exists
   * once that skin is applied.
   */
  initializeMessage() {
    // ── The new spine: one line per slot ── the same sentence, split where
    // _twoLines splits it ("BETTER LUCK" / "NEXT TIME").
    const skeleton = this.spine?.skeleton;
    if (skeleton?.findSlot(MESSAGE_TOP_SLOT) && skeleton?.findSlot(MESSAGE_BOTTOM_SLOT)) {
      // const [top, bottom = ""] = this._twoLines(
      //   this.t("better_luck_next_time").toUpperCase(),
      // ).split("\n");
      // The shared string (shared-translations 1.0.294): `wins_no_win`,
      // "Better luck\n next time" — already two lines; trimmed, because the
      // second carries a leading space.
      const [top = "", bottom = ""] = this._twoLines(
        this.t("wins_no_win").toUpperCase(),
      )
        .split("\n")
        .map((part) => part.trim());
      const line = (slotName, text) => {
        const container = new PIXI.Container();
        container.name = `free_spin_zero_win_${slotName}`;
        const textObj = new PIXI.Text(
          text,
          new PIXI.TextStyle(scaleStyle(FONT_STYLES.zeroWinText, MESSAGE_FONT_SIZE)),
        );
        textObj.resolution = 2;
        textObj.anchor.set(0.5);
        // On the TEXT — spine-pixi writes the bone's scale onto the container.
        if (textObj.width > MESSAGE_MAX_WIDTH) {
          textObj.scale.set(MESSAGE_MAX_WIDTH / textObj.width);
        }
        container.addChild(textObj);
        this.spine.addSlotObject(slotName, container);
        return textObj;
      };
      this.messageText = line(MESSAGE_TOP_SLOT, top);
      this.messageBottomText = line(MESSAGE_BOTTOM_SLOT, bottom);
      return;
    }
    const slotName = this._resolveSlot(MESSAGE_SLOTS);
    if (!slotName) {
      console.warn("[FreeSpinZeroWin] No message slot:", MESSAGE_SLOTS);
      return;
    }

    const container = new PIXI.Container();
    container.name = "free_spin_zero_win_message";

    // ── NO defaultValue ──
    //
    // `better_luck_next_time` is in neither namespace — not the game's
    // translations.json and not @popiplay/shared-translations. The default
    // that used to sit here rendered "BETTER LUCK NEXT TIME" in every
    // language and made a missing string look like a finished one, which is
    // how it survived to the second demo unnoticed.
    //
    // Without it i18next returns the KEY, so the popup says
    // `better_luck_next_time` until the string lands. Vladimir, 2026-09-22:
    // "we do not use backup or default values, default value should show that
    // we dont have string."
    const label = this._twoLines(this.t("better_luck_next_time").toUpperCase());

    this.messageText = new PIXI.Text(
      label,
      new PIXI.TextStyle(
        scaleStyle(FONT_STYLES.zeroWinText, MESSAGE_FONT_SIZE, {
          align: "center",
          // Without an explicit line height the two lines drift apart at this
          // size, because troika's own leading is generous.
          lineHeight: Math.round(MESSAGE_FONT_SIZE * MESSAGE_LINE_HEIGHT),
        }),
      ),
    );
    this.messageText.resolution = 2;
    this.messageText.anchor.set(0.5);
    container.addChild(this.messageText);

    this.spine.addSlotObject(slotName, container);
    this.messageContainer = container;
    this._messageSlotName = slotName;
    this._fitMessage();
  }

  /**
   * Break a phrase onto two lines: "BETTER LUCK" / "NEXT TIME".
   *
   * Split at the space nearest the middle rather than after a fixed word
   * count, so a translation of a different length still balances instead of
   * leaving one line far longer than the other. A string that already carries
   * a newline is left exactly as the translator wrote it.
   */
  _twoLines(text) {
    if (text.includes("\n")) return text;

    const spaces = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === " ") spaces.push(i);
    }
    if (!spaces.length) return text;

    const middle = text.length / 2;
    const at = spaces.reduce((best, i) =>
      Math.abs(i - middle) < Math.abs(best - middle) ? i : best,
    );
    return `${text.slice(0, at)}\n${text.slice(at + 1)}`;
  }

  /**
   * The value and the multiplier.
   *
   * `in` and `out` key BOTH slots alongside everything else, so leaving them
   * empty means the spine animates two blanks and the layout reads as though
   * something failed to load.
   *
   * ── These are NOT always zero ──
   *
   * They were, when this popup only ever meant "won nothing". The rule it now
   * serves is the GDD's: BETTER LUCK NEXT TIME replaces Total Win whenever the
   * round paid **less than 1x the stake** — so 0.4x bet is a perfectly valid
   * Better Luck round, and printing 0 for it would tell the player they won
   * nothing when they are about to be paid. See freeSpinOutroPopup.
   *
   * So both are BUILT here at zero and FILLED by setTotalWinValue, which the
   * dialog calls before startSequence. A round that really did pay nothing
   * lands on 0 by arithmetic rather than by assumption.
   *
   * Styled with winCelebrationCounter, the same as the total win popup, so the
   * two read as the same screen with a different number.
   */
  initializeValues() {
    const style = FONT_STYLES.winCelebrationCounter;

    // Both sized exactly as the base game's win popup and the other two
    // free-spin ones — same spine, same size. See WIN_TEXT_SCALE in fontStyles.
    this.valueText = this._addSlotText(
      this._resolveSlot(VALUE_SLOTS),
      formatToCustomLocale(0),
      style,
      Math.round(style.fontSize * WIN_TEXT_SCALE),
    );

    this.multiplierText = this._addSlotText(
      this._resolveSlot(MULTIPLIER_SLOTS),
      this._multiplierString(0),
      style,
      Math.round(FONT_STYLES.winCelebrationXBet.fontSize * WIN_TEXT_SCALE),
    );
  }

  /** "0.4x BET" — the total win popup's exact format, so the two read alike. */
  _multiplierString(multiplier) {
    const bet = this.t("bet").toUpperCase();
    return `${multiplier.toLocaleString("en-US")}x ${bet}`;
  }

  /**
   * The win, and the stake it was played at.
   *
   * Called by the dialog before startSequence, exactly as
   * FreeSpinTotalWinDialog does, so the figure is on screen from the first
   * frame instead of popping in.
   *
   * `bet` is the STAKE, never the price of a bought bonus — the same
   * `freeSpinOutroBet` that decided this popup should open at all. If the two
   * ever diverged, the player could read BETTER LUCK NEXT TIME above a line
   * saying 1.4x BET.
   *
   * There is no counter: a sub-1x win has nothing worth counting up to, and
   * the point of the popup is that the round fell short. The value is set.
   */
  setTotalWinValue(totalWin, bet) {
    const win = Number(totalWin) || 0;
    const stake = Number(bet) || 0;

    if (this.valueText) {
      this.valueText.text = formatToCustomLocale(win);
      this._fitSlotText(this.valueText);
    }

    if (this.multiplierText) {
      const multiplier = stake > 0 ? parseFloat((win / stake).toFixed(2)) : 0;
      this.multiplierText.text = this._multiplierString(multiplier);
      this._fitSlotText(this.multiplierText);
      // A round that paid NOTHING shows no xBet line — "0x BET" says nothing
      // (Vladimir, 2026-09-30). On the TEXT: spine-pixi rewrites the slot
      // container's own visibility every frame.
      this.multiplierText.visible = win > 0;
    }
  }

  /**
   * Shrink a slot text back inside its bone after its content changed.
   *
   * _addSlotText fits once, against the placeholder. A real value is longer
   * than "0" and "0x BET", so without this the number would overrun the plate.
   *
   * ── On the TEXT, never the container ──
   *
   * The spine rewrites a slot object's transform every frame, so a scale set
   * on the container is wiped on the next one. attachTapToContinue scales its
   * text for exactly this reason, and applyButtonOffset carries the same note
   * about the tap word's y.
   */
  _fitSlotText(textObj) {
    if (!textObj) return;
    const boneWidth = textObj._boneWidth || 0;
    textObj.scale.set(1);
    if (boneWidth > 0 && textObj.width > boneWidth) {
      textObj.scale.set(boneWidth / textObj.width);
    }
  }

  /**
   * One text, in one slot, fitted to its bone.
   * @returns {PIXI.Text|null}
   */
  _addSlotText(slotName, text, style, fontSize) {
    if (!slotName || !style) return null;
    const slot = this.spine.skeleton.findSlot(slotName);
    if (!slot) return null;

    const container = new PIXI.Container();
    container.name = `free_spin_zero_win_${slotName}`;

    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle(scaleStyle(style, fontSize)),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);
    container.addChild(textObj);

    // Remembered on the text so setTotalWinValue can re-fit after the string
    // changes, without re-reading the skeleton.
    textObj._boneWidth = slot.bone?.data?.length || 0;
    // Through the same helper the refit uses — scaling the container here and
    // the text there would leave two shrinks stacked on one overrun.
    this._fitSlotText(textObj);

    this.spine.addSlotObject(slotName, container);
    return textObj;
  }

  /** Shrink the block to the bone, then to the screen. Never scaled up. */
  _fitMessage() {
    if (!this.messageContainer || !this.messageText) return;
    const boneWidth =
      this.spine?.skeleton?.findSlot(this._messageSlotName)?.bone?.data
        ?.length || 0;
    const spineScale = this.spine?.scale?.x || 1;
    const screenLocal = ((this.dimensions?.width || 800) * 0.85) / spineScale;
    const maxWidth = boneWidth > 0 ? Math.min(boneWidth, screenLocal) : screenLocal;

    this.messageContainer.scale.set(1);
    if (maxWidth > 0 && this.messageText.width > maxWidth) {
      this.messageContainer.scale.set(maxWidth / this.messageText.width);
    }
  }

  initializeButton() {
    const built = attachTapToContinue({
      spine: this.spine,
      slotNames: BUTTON_SLOTS,
      label: this._buttonLabel(),
      name: "free_spin_zero_win_continue_attached_to_slot",
      onClick: () => {
        if (this.handleUserClick()) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      },
    });
    if (!built) return;
    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._setTapOffsetY = built.setOffsetY;
    this._revealMs = built.revealMs;
    this._fitTapButton(this.dimensions);
    this.applyButtonOffset();
  }

  /**
   * Lift the tap word off its bone, in SPINE UNITS.
   *
   * On the TEXT, because the spine rewrites the container's transform every
   * frame. Spine units rather than a multiple of symbolHeight: text.y is
   * spine-local, so the spine's own scale carries it, and the value can be
   * read straight off the bone geometry. The old form reduced to
   * `offset x SYMBOL_HEIGHT / deviceScale` anyway — the same constant, just
   * two indirections away from the numbers it had to match.
   */
  applyButtonOffset() {
    if (!this._setTapOffsetY) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this._setTapOffsetY(buttonYOffset);
  }

  _buttonLabel() {
    // TAP on every device — see FreeSpinOutroDialogController for why.
    return this.t("tap_to_continue").toUpperCase();
  }

  updateButtonText() {
    if (!this.buttonText) return;
    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  // ── Values ───────────────────────────────────────────────────────────────

  getSpineAnimationDuration(name) {
    const anim = this.spine?.skeleton?.data?.animations?.find(
      (a) => a.name === name,
    );
    return anim?.duration ? Math.round(anim.duration * 1000) : 1000;
  }

  _clearTimeouts() {
    for (const key of [
      "skipProtectionTimeout",
      "debounceTimeout",
      "inIdleTimeout",
      "musicDelayTimeout",
      "autoCloseTimeout",
    ]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  startSequence() {
    if (this.isPlaying || !this.spine?.state) return;

    this.isPlaying = true;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this._clearTimeouts();

    this.overlayRef?.current?.fadeInBackground();

    // Duck whichever feature track is running — they are exclusive — and
    // remember which, so the round can restore it afterwards.
    this._wasAmbientActive = soundManager.isAmbientActive;
    const runningTrack = this._wasAmbientActive
      ? SOUND_NAME.feature_ambient_music
      : SOUND_NAME.feature_background_music;
    if (HowlCache[runningTrack]?.playing()) {
      soundManager.fadeOut(runningTrack, MUSIC_FADE_MS);
    }

    // ── It gets its OWN sting, not silence ──
    //
    // This used to play nothing, on the reasoning that nothing was won. But a
    // silent popup after a ducked track reads as the sound breaking, not as a
    // statement — and this screen IS a statement: the round is over and it
    // paid nothing. `no_win` says that, at the same level as total_win and
    // max_win say their versions of it.
    //
    // Same beat as the other two: after the duck, before the spine starts.
    soundManager.play(SOUND_NAME.no_win);

    this.applyScaleAndCenter();

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.state.apply(this.spine.skeleton);
    this.spine.skeleton.setToSetupPose();
    // setToSetupPose reverts the skin's slot state, so re-apply after it.
    this.applySkin();

    this.skipProtectionTimeout = new TickerTimeout(() => {
      // Only unlocks INPUT; isWaitingForClick is set by _startAutoClose.
      if (this.isPlaying) this.canSkipAnimation = true;
    }, this._revealMs?.("in") ?? SKIP_PROTECTION_MS);

    // Played while STILL HIDDEN, shown on the NEXT frame.
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "in", false);
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) this.spine.alpha = 1;
    });

    const inDuration = this.getSpineAnimationDuration("in");
    this.inIdleTimeout = new TickerTimeout(() => {
      if (this.isPlaying) {
        if (this.spine) this.spine.autoUpdate = true;
        this.spine.state.setAnimation(0, "idle", true);
      }
    }, inDuration);

    // Nothing to count, so the popup is dismissable as soon as skip protection
    // lifts. _startAutoClose is what sets isWaitingForClick.
    this._startAutoClose();
  }

  /** One stage — there is no counter to skip past. */
  handleUserClick() {
    if (!this.isPlaying || !this.spine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation || !this.isWaitingForClick) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, CLICK_DEBOUNCE_MS);

    this.playClosingAnimation();
    return true;
  }

  /** Auto-close, and the point at which a tap is accepted. */
  _startAutoClose() {
    this.isWaitingForClick = true;
    this.autoCloseTimeout?.clear();
    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying) this.playClosingAnimation();
    }, AUTO_CLOSE_DELAY);
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.spine?.state) return;
    this.isWaitingForClick = false;

    this.spine.state.clearListeners();
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, "out", false);
    this.spine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Restore whichever feature track was ducked when we opened. The
          // duck was carried over from Pot Collection; this half was not, so
          // the game stayed silent after the popup closed.
          soundManager.fadeIn(
            this._wasAmbientActive
              ? SOUND_NAME.feature_ambient_music
              : SOUND_NAME.feature_background_music,
            MUSIC_FADE_MS,
          );
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Close.
   *
   * The flag is dispatched SYNCHRONOUSLY, and that is not a style choice.
   * Every other popup clears it inside the overlay-fade callback, which is
   * safe because nothing is still asking for them. This one is dispatched at
   * the END of the feature, so the condition that opened it is still true for
   * as long as the flag is set — clearing it a fade later leaves a window in
   * which the trigger can fire again and the popup reopens. The Pot Collection
   * outro carries the same ordering, under a comment reading "CRITICAL: Close
   * the dialog FIRST (synchronous) to prevent re-trigger."
   *
   * The overlay fade is purely visual by then; the dialog is already closed.
   *
   * What FOLLOWS is the scene wipe back to the base game, started here on the
   * same handover FreeSpinTotalWinController and FreeSpinMaxWinController use.
   * The wipe's own midpoint tears the round down under full cover.
   *
   * ── This is the popup's only way home ──
   *
   * Nothing else watches this flag drop. While this popup was unreachable the
   * omission cost nothing; the moment a sub-1x round ends here instead of on
   * the total, leaving it out strands the player in the bonus room with a
   * closed dialog and no wipe.
   */
  finishAnimation() {
    if (!this.spine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;

    this.spine.alpha = 0;
    if (this.spine) this.spine.autoUpdate = false;
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    
    // FIRST, and synchronous. See above.
    this.dispatch?.(setShowFreeSpinZeroWin(false));
    // Then the way out. The wipe covers the screen and resets the round at its
    // midpoint, so the player never sees the rooms change back.
    // this.dispatch?.(setShowTransitionDialog(true));
    // The dev panel's preview (no free-spins room up) just closes — no wipe
    // out of a room that is not there. Live play is unchanged (2026-09-30).
    const devPreview =
      FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL && !store.getState().freeSpins?.active;
    if (!devPreview) this.dispatch?.(setShowTransitionDialog(true));
    this.overlayRef?.current?.fadeOutBackground();
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.spine.removeSlotObjects) this.spine.removeSlotObjects();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    this.messageText = null;
    this.messageContainer = null;
    this.valueText = null;
    this.multiplierText = null;
    this.buttonContainer = null;
    this.buttonText = null;
    this._fitTapButton = null;
    this.initialized = false;
  }
}

export default FreeSpinZeroWinController;
